import { Vehicle } from "./types";
import { BookingRequest, BookingResponse, BookingActionResponse } from "@/types/booking";
import { vehicleService, bookingService } from "./services";

export const vehiclesApi = {
  async getVehicles() {
    return await vehicleService.searchVehicles({});
  },

  async getAvailableCities() {
    return await vehicleService.getAvailableCities();
  },

  async searchVehicles(params: {
    location?: string;
    startDate?: string;
    endDate?: string;
    category?: string;
    minPrice?: number;
    maxPrice?: number;
    transmission?: string;
    fuelType?: string;
    minSeats?: number;
    isPremium?: boolean;
    features?: string[];
    colors?: string | string[]; // Filtre par couleur(s)
    cancellationPolicy?: "flexible" | "moderate" | "strict";
    fuelPolicy?: string;
    /** When true, only return vehicles with no deposit (sans caution) */
    noDeposit?: boolean;
  }) {
    if (import.meta.env.DEV) {
      console.log("Recherche de véhicules avec les paramètres:", params);
    }

    const vehicles = await vehicleService.searchVehicles({
      location: params.location,
      min_price: params.minPrice,
      max_price: params.maxPrice,
      transmission: params.transmission,
      fuel_type: params.fuelType,
      seats: params.minSeats,
      status: "available",
      features: params.features,
      color: params.colors,
      category: params.category,
      cancellation_policy: params.cancellationPolicy,
      fuel_policy: params.fuelPolicy,
      no_deposit: params.noDeposit,
      start_date: params.startDate,
      end_date: params.endDate,
    });

    if (import.meta.env.DEV) {
      console.log(`${vehicles.length} véhicules récupérés depuis Supabase`);
    }

    // Apply client-side filters
    let filtered = [...vehicles];

    if (params.transmission && params.transmission !== "all") {
      filtered = filtered.filter((v) => v.transmission === params.transmission);
    }

    if (params.fuelType && params.fuelType !== "all") {
      filtered = filtered.filter((v) => v.fuel_type === params.fuelType);
    }

    if (params.minSeats !== undefined) {
      filtered = filtered.filter((v) => (v.seats || 0) >= params.minSeats!);
    }

    if (params.isPremium !== undefined) {
      filtered = filtered.filter((v) => (v.is_premium || false) === params.isPremium);
    }

    if (import.meta.env.DEV) {
      console.log(`${filtered.length} véhicules après filtrage client-side`);
    }
    return filtered;
  },

  async getVehicle(id: string) {
    if (import.meta.env.DEV) {
      console.log("Tentative de récupération du véhicule avec l'ID:", id);
    }
    const vehicle = await vehicleService.getVehicleById(id);
    
    // Charger les extras du véhicule si le véhicule existe
    if (vehicle?.id) {
      try {
        const extras = await vehicleService.getVehicleExtras(vehicle.id);
        vehicle.extras = extras;
        if (import.meta.env.DEV) {
          console.log(`✅ ${extras.length} option(s) extra chargée(s) pour le véhicule ${vehicle.id}`);
        }
      } catch (extrasError: any) {
        console.error("Erreur lors du chargement des extras:", extrasError);
        // Ne pas bloquer si les extras ne peuvent pas être chargés
        vehicle.extras = [];
      }
    }
    
    if (import.meta.env.DEV) {
      console.log("Véhicule formaté avec extras:", vehicle);
    }
    
    return vehicle;
  },

  async getRecommendedVehicles(vehicleId: string, location: string, pricePerDay: number) {
    return vehicleService.getRecommendedVehicles(vehicleId, location, pricePerDay);
  },

  async getAgencyVehicles(ownerId: string) {
    return vehicleService.getAgencyVehicles(ownerId);
  },

  async createVehicle(vehicle: Omit<Vehicle, "id" | "created_at" | "owner_id">) {
    const vehicleData: Record<string, unknown> = {
      make: vehicle.make || vehicle.brand || "",
      model: vehicle.model || "",
      year: vehicle.year || new Date().getFullYear(),
      price_per_day: vehicle.price_per_day || vehicle.price || 0,
      location: vehicle.location || "",
      description: vehicle.description,
      images: vehicle.images || [],
      fuel_type: vehicle.fuel_type || vehicle.fuel,
      transmission: vehicle.transmission,
      seats: vehicle.seats,
      luggage: vehicle.luggage,
      mileage: vehicle.mileage,
      color: vehicle.color,
      features: vehicle.features || [],
      category: vehicle.category,
      latitude: vehicle.latitude,
      longitude: vehicle.longitude,
      is_premium: vehicle.is_premium || vehicle.isPremium,
      delivery_zone: vehicle.delivery_zone,
      deposit: vehicle.deposit,
      deductible: vehicle.deductible,
      cancellation_policy: vehicle.cancellation_policy,
      fuel_policy: vehicle.fuel_policy,
      status: "unavailable" as const, // Unavailable jusqu'à approbation
      publication_status: "pending_review" as const, // Nouveau véhicule soumis à vérification
    };

    // Ajouter pickup_locations seulement s'il est défini
    if (vehicle.pickup_locations !== undefined) {
      vehicleData.pickup_locations = vehicle.pickup_locations;
    }

    // Ajouter return_locations seulement s'il est défini
    if (vehicle.return_locations !== undefined) {
      vehicleData.return_locations = vehicle.return_locations;
    }

    // Filtrer les propriétés undefined
    const filteredData = Object.fromEntries(
      Object.entries(vehicleData).filter(([_, value]) => value !== undefined)
    );

    const newVehicle = await vehicleService.createVehicle(filteredData);
    console.log("Véhicule créé avec succès:", newVehicle);
    return newVehicle;
  },

  async updateVehicle(id: string, vehicle: Partial<Vehicle>) {
    const updateData: Record<string, unknown> = {};

    if (vehicle.make) updateData.make = vehicle.make;
    if (vehicle.brand) updateData.make = vehicle.brand;
    if (vehicle.model) updateData.model = vehicle.model;
    if (vehicle.year) updateData.year = vehicle.year;
    if (vehicle.price_per_day) updateData.price_per_day = vehicle.price_per_day;
    if (vehicle.price) updateData.price_per_day = vehicle.price;
    if (vehicle.location) updateData.location = vehicle.location;
    if (vehicle.description !== undefined) updateData.description = vehicle.description;
    if (vehicle.images) updateData.images = vehicle.images;
    if (vehicle.fuel_type) updateData.fuel_type = vehicle.fuel_type;
    if (vehicle.fuel) updateData.fuel_type = vehicle.fuel;
    if (vehicle.transmission) updateData.transmission = vehicle.transmission;
    if (vehicle.seats) updateData.seats = vehicle.seats;
    if (vehicle.luggage) updateData.luggage = vehicle.luggage;
    if (vehicle.mileage) updateData.mileage = vehicle.mileage;
    if (vehicle.color) updateData.color = vehicle.color;
    if (vehicle.features) updateData.features = vehicle.features;
    if (vehicle.category) updateData.category = vehicle.category;
    if (vehicle.latitude !== undefined) updateData.latitude = vehicle.latitude;
    if (vehicle.longitude !== undefined) updateData.longitude = vehicle.longitude;
    if (vehicle.is_premium !== undefined) updateData.is_premium = vehicle.is_premium;
    if (vehicle.isPremium !== undefined) updateData.is_premium = vehicle.isPremium;

    // Champs manquants pour la localisation et les politiques
    if (vehicle.pickup_locations !== undefined) updateData.pickup_locations = vehicle.pickup_locations;
    if (vehicle.return_locations !== undefined) updateData.return_locations = vehicle.return_locations;
    if (vehicle.delivery_zone !== undefined) updateData.delivery_zone = vehicle.delivery_zone;
    if (vehicle.deposit !== undefined) updateData.deposit = vehicle.deposit;
    if (vehicle.deductible !== undefined) updateData.deductible = vehicle.deductible;
    if (vehicle.cancellation_policy !== undefined) updateData.cancellation_policy = vehicle.cancellation_policy;
    if (vehicle.fuel_policy !== undefined) updateData.fuel_policy = vehicle.fuel_policy;
    if (vehicle.publication_status !== undefined) updateData.publication_status = vehicle.publication_status;

    const updatedVehicle = await vehicleService.updateVehicle(id, updateData);
    return updatedVehicle;
  },

  async deleteVehicle(id: string) {
    await vehicleService.deleteVehicle(id);
  },
};

export const bookingsApi = {
  // Récupérer les réservations d'un locataire
  async getRenterBookings() {
    try {
      const bookings = await bookingService.getRenterBookings();
      return { data: bookings, error: null };
    } catch (error: unknown) {
      return { data: [], error: error instanceof Error ? error : new Error(String(error)) };
    }
  },

  // Créer une nouvelle réservation
  async createBooking(
    request: BookingRequest & {
      renterId: string;
      ownerId: string;
      basePrice: number;
      insuranceFee: number;
      serviceFee: number;
      totalPrice: number;
      depositAmount: number;
      durationDays: number;
    }
  ): Promise<BookingResponse> {
    try {
      const booking = await bookingService.createBooking({
        vehicle_id: request.vehicleId,
        start_date: request.startDate,
        end_date: request.endDate,
        total_price: request.totalPrice,
        deposit_amount: request.depositAmount,
      });

      return {
        success: true,
        bookingId: booking.id,
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to create booking",
      };
    }
  },

  // Annuler une réservation
  async cancelBooking(bookingId: string): Promise<BookingActionResponse> {
    try {
      await bookingService.cancelBooking(bookingId);
      return {
        success: true,
        message: "Réservation annulée avec succès",
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to cancel booking",
      };
    }
  },

  // Mettre à jour le statut d'une réservation (confirmer)
  async updateBookingStatus(bookingId: string, status: string): Promise<BookingActionResponse> {
    try {
      if (status === "confirmed") {
        await bookingService.confirmBooking(bookingId);
      } else if (status === "cancelled") {
        await bookingService.cancelBooking(bookingId);
      }

      return {
        success: true,
        message: "Statut mis à jour avec succès",
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to update booking status",
      };
    }
  },

  // Get booking by ID
  async getBookingById(bookingId: string) {
    try {
      const booking = await bookingService.getBookingById(bookingId);
      return { data: booking, error: null };
    } catch (error: unknown) {
      return { data: null, error: error instanceof Error ? error : new Error(String(error)) };
    }
  },

  // Get owner bookings
  async getOwnerBookings() {
    try {
      const bookings = await bookingService.getOwnerBookings();
      return { data: bookings, error: null };
    } catch (error: unknown) {
      return { data: [], error: error instanceof Error ? error : new Error(String(error)) };
    }
  },
};
